import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Avatar,
  Chip,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Fab,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import {
  Send as SendIcon,
  Add as AddIcon,
  Chat as ChatIcon,
  ArrowBack as ArrowBackIcon,
  ViewSidebarOutlined as ViewSidebarOutlinedIcon,
} from '@mui/icons-material';
import { messagingService } from '../../../services/messaging.service';
import { useAuth } from '../../../context/AuthContext';
import { theme } from '../../../theme';
import { useMessagingWebSocket } from '../../../hooks/useMessagingWebSocket';

const STATUS_COLORS = {
  open: 'warning',
  assigned: 'info',
  closed: 'default',
};

const SalonManagerMessaging = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newConversationSubject, setNewConversationSubject] = useState('');
  const [newConversationMessage, setNewConversationMessage] = useState('');
  const [newConversationPriority, setNewConversationPriority] = useState('medium');
  const [supportSalons, setSupportSalons] = useState([]);
  const [selectedSalonId, setSelectedSalonId] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [mobilePane, setMobilePane] = useState('list');
  const [isConversationListCollapsed, setIsConversationListCollapsed] = useState(false);
  const messagesEndRef = useRef(null);
  const isPhone = useMediaQuery('(max-width:599.95px)');

  const joinConversationRef = useRef(null);

  const handleWsMessage = useCallback((data) => {
    if (!data) return;

    if (data.type === 'new_message' && data.message) {
      const message = data.message;

      setConversations((prev) => {
        const next = [...prev];
        const idx = next.findIndex((c) => c.id === message.conversation_id);
        if (idx === -1) return prev;

        next[idx] = {
          ...next[idx],
          last_message: message,
        };

        return next;
      });

      if (message.conversation_id === selectedConversation?.id) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === message.id)) return prev;
          return [...prev, message];
        });
      }

      return;
    }

    if (data.type === 'new_conversation') {
      fetchConversations();
    }
  }, [selectedConversation?.id]);

  const { status: wsStatus, sendJson } = useMessagingWebSocket({
    enabled: !!user?.token,
    token: user?.token,
    onMessage: handleWsMessage,
    onOpen: () => {
      if (typeof joinConversationRef.current === 'function') {
        joinConversationRef.current();
      }
    },
  });

  useEffect(() => {
    fetchConversations();
  }, [showArchived]);

  useEffect(() => {
    const fetchSupportSalons = async () => {
      try {
        const response = await messagingService.getSupportSalons();
        const salons = Array.isArray(response.data) ? response.data : [];
        setSupportSalons(salons);
        setSelectedSalonId((current) => {
          if (salons.some((salon) => Number(salon.salon_id) === Number(current))) {
            return current;
          }
          return salons[0]?.salon_id ?? '';
        });
      } catch (error) {
        console.error('Error fetching support salons:', error);
        setSupportSalons([]);
        setSelectedSalonId('');
      }
    };

    fetchSupportSalons();
  }, []);

  const handleToggleArchive = async () => {
    if (!selectedConversation?.id) return;

    try {
      if (selectedConversation.is_archived) {
        await messagingService.unarchiveConversation(selectedConversation.id);
      } else {
        await messagingService.archiveConversation(selectedConversation.id);
      }

      setSelectedConversation(null);
      setMessages([]);
      if (isPhone) setMobilePane('list');
      fetchConversations();
    } catch (error) {
      console.error('Error toggling archive:', error);
    }
  };

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.id);
    }
  }, [selectedConversation]);

  useEffect(() => {
    if (!selectedConversation?.id) {
      joinConversationRef.current = null;
      return;
    }

    joinConversationRef.current = () => {
      sendJson({
        action: 'join_conversation',
        conversation_id: selectedConversation.id,
      });
    };

    if (wsStatus === 'open') {
      joinConversationRef.current();
    }
  }, [selectedConversation?.id, sendJson, wsStatus]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const response = await messagingService.getMyConversations({ archived: showArchived });
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const response = await messagingService.retrieveConversation(conversationId);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    const content = newMessage.trim();
    setNewMessage('');

    try {
      const sent = sendJson({
        action: 'send_message',
        conversation_id: selectedConversation.id,
        content,
      });

      if (!sent) {
        await messagingService.sendMessage(selectedConversation.id, content);
        fetchMessages(selectedConversation.id);
        fetchConversations();
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setNewMessage(content);
    }
  };

  const handleCreateConversation = async () => {
    if (
      !selectedSalonId
      || !newConversationSubject.trim()
      || !newConversationMessage.trim()
    ) return;

    try {
      const payload = {
        salon_id: Number(selectedSalonId),
        subject: newConversationSubject.trim(),
        initial_message: newConversationMessage.trim(),
        priority: newConversationPriority,
      };
      
      await messagingService.createConversation(payload);
      setCreateDialogOpen(false);
      setNewConversationSubject('');
      setNewConversationMessage('');
      setNewConversationPriority('medium');
      fetchConversations();
    } catch (error) {
      console.error('Error creating conversation:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    if (isPhone) setMobilePane('chat');
  };

  const showConversationList = isPhone ? mobilePane === 'list' : !isConversationListCollapsed;
  const showChat = isPhone ? mobilePane === 'chat' : true;

  return (
    <Box sx={{ display: 'flex', width: '100%', minWidth: 0, overflow: 'hidden', height: 'calc(100vh - 200px)', gap: isPhone ? 0 : 2 }}>
      {/* Conversations List */}
      {showConversationList && (
      <Paper
        data-testid="conversation-list-panel"
        sx={{ width: isPhone ? '100%' : 350, maxWidth: '100%', flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <Box sx={{ p: 2, borderBottom: `1px solid ${theme.colors.border.light}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t('messaging.support_saloone')}
            </Typography>
            {!isPhone && (
              <Tooltip title={t('messaging.hide_conversations')}>
                <IconButton
                  aria-label={t('messaging.hide_conversations')}
                  onClick={() => setIsConversationListCollapsed(true)}
                >
                  <ViewSidebarOutlinedIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('messaging.contact_platform_support')}
          </Typography>

          <Button
            fullWidth
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{ mb: 2 }}
          >
            {t('messaging.new_conversation')}
          </Button>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              size="small"
              label={showArchived ? t('messaging.archived') : t('messaging.active')}
              color={showArchived ? 'default' : 'primary'}
              variant={showArchived ? 'outlined' : 'filled'}
              onClick={() => setShowArchived((prev) => !prev)}
            />
          </Box>
        </Box>

        <List sx={{ flex: 1, overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : conversations.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                {t('messaging.no_conversation')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('messaging.no_conversation_desc')}
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
              >
                {t('messaging.new_conversation')}
              </Button>
            </Box>
          ) : (
            conversations.map((conversation) => (
              <ListItem
                key={conversation.id}
                button
                selected={selectedConversation?.id === conversation.id}
                onClick={() => handleSelectConversation(conversation)}
                sx={{
                  borderBottom: `1px solid ${theme.colors.border.light}`,
                  '&.Mui-selected': {
                    backgroundColor: theme.colors.primary[50],
                  },
                }}
              >
                <ListItemAvatar>
                  <Badge
                    badgeContent={conversation.unread_count}
                    color="error"
                    invisible={conversation.unread_count === 0}
                  >
                    <Avatar sx={{ bgcolor: theme.colors.primary[500] }}>
                      <ChatIcon />
                    </Avatar>
                  </Badge>
                </ListItemAvatar>
                <ListItemText
                  primaryTypographyProps={{ component: 'div' }}
                  secondaryTypographyProps={{ component: 'div' }}
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
                        {conversation.subject}
                      </Typography>
                      <Chip
                        size="small"
                        label={t(`messaging.status.${conversation.status}`)}
                        color={STATUS_COLORS[conversation.status]}
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {conversation.last_message?.content || t('messaging.no_message')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {conversation.assigned_to 
                          ? `${t('messaging.assigned_to')} ${conversation.assigned_to.first_name} ${conversation.assigned_to.last_name}`
                          : t('messaging.waiting_assignment')}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))
          )}
        </List>
      </Paper>
      )}

      {/* Chat Area */}
      {showChat && (
      <Paper
        data-testid="conversation-chat-panel"
        sx={{ position: 'relative', flex: 1, minWidth: 0, width: isPhone ? '100%' : 'auto', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        {!isPhone && isConversationListCollapsed && (
          <Tooltip title={t('messaging.show_conversations')}>
            <IconButton
              aria-label={t('messaging.show_conversations')}
              onClick={() => setIsConversationListCollapsed(false)}
              sx={{ position: 'absolute', top: 8, left: 8, zIndex: 1 }}
            >
              <ViewSidebarOutlinedIcon />
            </IconButton>
          </Tooltip>
        )}
        {selectedConversation ? (
          <>
            {/* Header */}
            <Box sx={{ p: 2, pl: !isPhone && isConversationListCollapsed ? 7 : 2, borderBottom: `1px solid ${theme.colors.border.light}` }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {isPhone && (
                    <Tooltip title={t('messaging.back_to_conversations')}>
                      <IconButton
                        aria-label={t('messaging.back_to_conversations')}
                        onClick={() => setMobilePane('list')}
                        edge="start"
                      >
                        <ArrowBackIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Typography variant="h6" sx={{ fontWeight: 700 }} noWrap>
                    {selectedConversation.subject}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleToggleArchive}
                >
                  {selectedConversation.is_archived ? t('messaging.unarchive') : t('messaging.archive')}
                </Button>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Chip
                  size="small"
                  label={t(`messaging.priority_labels.${selectedConversation.priority}`)}
                  color={selectedConversation.priority === 'urgent' ? 'error' : 'default'}
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
                {selectedConversation.assigned_to && (
                  <Typography variant="body2" color="text.secondary">
                    {t('messaging.in_charge')} {selectedConversation.assigned_to.first_name} {selectedConversation.assigned_to.last_name}
                  </Typography>
                )}
              </Box>
              {selectedConversation.status === 'closed' && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  {t('messaging.conversation_closed')}
                </Alert>
              )}
            </Box>

            {/* Messages */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: theme.colors.grey[50] }}>
              {messages.length === 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography color="text.secondary">
                    {t('messaging.no_message_in_conversation')}
                  </Typography>
                </Box>
              ) : (
                messages.map((message) => {
                  const isOwn = message.sender?.id === user?.id;
                  return (
                    <Box
                      key={message.id}
                      sx={{
                        display: 'flex',
                        justifyContent: isOwn ? 'flex-end' : 'flex-start',
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{
                          maxWidth: '70%',
                          display: 'flex',
                          flexDirection: isOwn ? 'row-reverse' : 'row',
                          alignItems: 'flex-start',
                          gap: 1,
                        }}
                      >
                        <Avatar sx={{ width: 32, height: 32, bgcolor: isOwn ? theme.colors.primary[500] : theme.colors.grey[400] }}>
                          {message.sender?.first_name?.[0] || 'A'}
                        </Avatar>
                        <Box>
                          <Paper
                            sx={{
                              p: 2,
                              bgcolor: isOwn ? theme.colors.primary[500] : '#fff',
                              color: isOwn ? '#fff' : 'text.primary',
                              borderRadius: 2,
                            }}
                          >
                            <Typography variant="body1">{message.content}</Typography>
                          </Paper>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            {message.sender?.first_name} {message.sender?.last_name} • {new Date(message.created_at).toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </Box>

            {/* Input */}
            {selectedConversation.status !== 'closed' && (
              <Box sx={{ p: 2, borderTop: `1px solid ${theme.colors.border.light}` }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    multiline
                    maxRows={4}
                    placeholder={t('messaging.write_message')}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                  />
                  <Button
                    variant="contained"
                    endIcon={<SendIcon />}
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim()}
                  >
                    {t('messaging.send_message')}
                  </Button>
                </Box>
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', p: 4 }}>
            <ChatIcon sx={{ fontSize: 64, color: theme.colors.grey[300], mb: 2 }} />
            <Typography color="text.secondary" align="center">
              {t('messaging.select_conversation')}
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
              {t('messaging.or_start_new')}
            </Typography>
          </Box>
        )}
      </Paper>
      )}

      {/* Floating Action Button to create new conversation */}
      <Fab
        color="primary"
        sx={{ position: 'fixed', bottom: 24, right: 24 }}
        onClick={() => setCreateDialogOpen(true)}
      >
        <AddIcon />
      </Fab>

      {/* Create Conversation Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isPhone}>
        <DialogTitle>{t('messaging.new_conversation')}</DialogTitle>
        <DialogContent>
          {supportSalons.length > 1 && (
            <TextField
              select
              fullWidth
              label={t('messaging.select_support_salon')}
              value={selectedSalonId}
              onChange={(event) => setSelectedSalonId(event.target.value)}
              SelectProps={{ native: true }}
              sx={{ mt: 1, mb: 2 }}
            >
              {supportSalons.map((salon) => (
                <option key={salon.salon_id} value={salon.salon_id}>
                  {salon.name}
                </option>
              ))}
            </TextField>
          )}
          <TextField
            fullWidth
            label={t('messaging.subject')}
            value={newConversationSubject}
            onChange={(e) => setNewConversationSubject(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
            placeholder={t('messaging.subject_placeholder')}
          />
          <TextField
            select
            fullWidth
            label={t('messaging.priority')}
            value={newConversationPriority}
            onChange={(e) => setNewConversationPriority(e.target.value)}
            sx={{ mb: 2 }}
            SelectProps={{ native: true }}
          >
            <option value="low">{t('messaging.priority_labels.low')}</option>
            <option value="medium">{t('messaging.priority_labels.medium')}</option>
            <option value="high">{t('messaging.priority_labels.high')}</option>
            <option value="urgent">{t('messaging.priority_labels.urgent')}</option>
          </TextField>
          <TextField
            fullWidth
            multiline
            rows={4}
            label={t('messaging.initial_message')}
            value={newConversationMessage}
            onChange={(e) => setNewConversationMessage(e.target.value)}
            placeholder={t('messaging.message_placeholder')}
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('messaging.message_sent_info')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>{t('messaging.cancel')}</Button>
          <Button 
            onClick={handleCreateConversation} 
            variant="contained"
            disabled={
              !selectedSalonId
              || !newConversationSubject.trim()
              || !newConversationMessage.trim()
            }
          >
            {t('messaging.send_message')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalonManagerMessaging;
